import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { PageBanner } from "@/components/ui/PageBanner";
import { IMG } from "@/lib/assets";
import { addDays, isoDay } from "@/lib/perform";
import { AlertTriangle, Flame, Award, Lock, ChevronLeft, ChevronRight } from "lucide-react";

const DOMAIN_COLOR = { strength: "#CCFF00", olympic: "#B57BFF", gymnastics: "#3D7BFF", engine: "#2DD4BF", metcon: "#FF5A5F" };

function Ring({ value }) {
  const r = 70, c = 2 * Math.PI * r, v = value || 0;
  return (
    <div className="relative w-44 h-44">
      <svg viewBox="0 0 160 160" className="w-full h-full -rotate-90">
        <circle cx="80" cy="80" r={r} stroke="#1E222A" strokeWidth="12" fill="none" />
        <motion.circle cx="80" cy="80" r={r} stroke="url(#lvl)" strokeWidth="12" fill="none" strokeLinecap="round"
          initial={{ strokeDashoffset: c }} animate={{ strokeDashoffset: c * (1 - v / 100) }} transition={{ duration: 1.2, ease: "easeOut" }} strokeDasharray={c} />
        <defs><linearGradient id="lvl" x1="0" x2="1"><stop offset="0" stopColor="#3D7BFF" /><stop offset="1" stopColor="#CCFF00" /></linearGradient></defs>
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <p className="font-heading font-black text-5xl">{value ?? "–"}</p>
        <p className="mono-label">fitness level</p>
      </div>
    </div>
  );
}

function FitnessLevel() {
  const navigate = useNavigate();
  const { data: d } = useQuery({ queryKey: ["fitness-level"], queryFn: async () => (await api.get("/api/performance/fitness-level")).data });
  if (!d) return <p className="mono-label animate-pulse">loading…</p>;
  return (
    <div className="surface rounded-2xl p-5 md:p-6" data-testid="fitness-level">
      <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 items-center">
        <div className="mx-auto"><Ring value={d.overall} /></div>
        <div className="space-y-3">
          {d.domains.map((x, i) => (
            <div key={x.key}>
              <div className="flex justify-between text-sm"><span>{x.label}</span>
                <span className="font-mono2" style={{ color: x.level ? DOMAIN_COLOR[x.key] : "#565C6F" }}>{x.level ?? "log a result"}</span></div>
              <div className="h-2.5 rounded-full bg-[#1E222A] overflow-hidden mt-1">
                <motion.div className="h-full rounded-full" style={{ background: DOMAIN_COLOR[x.key] }} initial={{ width: 0 }} animate={{ width: `${x.level || 0}%` }} transition={{ duration: 0.9, delay: i * 0.08 }} />
              </div>
            </div>
          ))}
        </div>
      </div>
      {d.imbalances.length > 0 && (
        <div className="mt-5 space-y-2">
          {d.imbalances.map((m) => (
            <p key={m} className="text-sm rounded-lg border border-[#FFB340]/40 bg-[#FFB340]/5 p-3 flex gap-2"><AlertTriangle className="w-4 h-4 text-[#FFB340] shrink-0 mt-0.5" /> {m}</p>
          ))}
        </div>
      )}
      {d.overall == null && (
        <div className="mt-4 text-sm text-[#8A91A6]">Log a few benchmarks (a lift, a Girl, a row or run) and your level appears.
          <button onClick={() => navigate("/app/benchmarks")} className="text-acid ml-1 hover:underline">Go to Benchmarks</button></div>
      )}
      <p className="text-xs text-[#565C6F] mt-4">{d.note}</p>
    </div>
  );
}

function Heatmap() {
  const [year, setYear] = useState(new Date().getFullYear());
  const { data } = useQuery({ queryKey: ["training-days", year], queryFn: async () => (await api.get("/api/performance/training-days", { params: { year } })).data });
  const set = useMemo(() => new Set(data?.days || []), [data]);
  const start = useMemo(() => { const j = new Date(year, 0, 1); return addDays(isoDay(j), -((j.getDay() + 6) % 7)); }, [year]);
  const weeks = Array.from({ length: 53 }, (_, w) => Array.from({ length: 7 }, (_, d) => addDays(start, w * 7 + d)));
  return (
    <div className="surface rounded-2xl p-5 md:p-6" data-testid="training-days">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <p className="font-heading font-bold">Training days</p>
          <p className="text-xs text-[#8A91A6]">Every day you logged training, in one view.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setYear(year - 1)} className="p-1.5 rounded-full border border-[#343A4A]" aria-label="Previous year"><ChevronLeft className="w-4 h-4" /></button>
          <span className="font-heading font-bold">{year}</span>
          <button onClick={() => setYear(year + 1)} disabled={year >= new Date().getFullYear()} className="p-1.5 rounded-full border border-[#343A4A] disabled:opacity-30" aria-label="Next year"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-3 mb-4">
        {[["days", data?.total], ["current streak", data?.current_streak], ["longest streak", data?.longest_streak], ["per week", data?.avg_per_week]].map(([l, v]) => (
          <div key={l} className="rounded-xl border border-[#1E222A] p-3"><p className="font-heading font-black text-2xl">{v ?? "–"}</p><p className="mono-label">{l}</p></div>
        ))}
      </div>
      <div className="overflow-x-auto">
        <div className="grid gap-[3px] min-w-[640px]" style={{ gridTemplateColumns: "repeat(53, minmax(0, 1fr))" }}>
          {weeks.map((w, i) => (
            <div key={i} className="grid gap-[3px]">
              {w.map((d) => (
                <div key={d} title={d} className="aspect-square rounded-[3px]"
                  style={{ background: Number(d.slice(0, 4)) !== year ? "transparent" : set.has(d) ? "#CCFF00" : "#1A1D24",
                    boxShadow: set.has(d) ? "0 0 8px rgba(204,255,0,0.45)" : "none" }} />
              ))}
            </div>
          ))}
        </div>
        <div className="flex justify-between mt-2 min-w-[640px]">
          {["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"].map((m) => <span key={m} className="mono-label">{m}</span>)}
        </div>
      </div>
    </div>
  );
}

function Badges() {
  const { data = [] } = useQuery({ queryKey: ["badges"], queryFn: async () => (await api.get("/api/badges")).data });
  const earned = data.filter((b) => b.earned).length;
  return (
    <div className="surface rounded-2xl p-5 md:p-6" data-testid="badges">
      <div className="flex items-center justify-between mb-4">
        <p className="font-heading font-bold flex items-center gap-2"><Award className="w-4 h-4 text-acid" /> Badges</p>
        <p className="mono-label text-acid">{earned} of {data.length}</p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {data.map((b, i) => (
          <motion.div key={b.key} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.02 }}
            className={`relative rounded-xl border p-3 text-center ${b.earned ? "border-acid/50 bg-gradient-to-b from-[#1A2108] to-[#0B0D11]" : "border-[#1E222A] bg-[#0B0D11]"}`}>
            <div className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center ${b.earned ? "bg-acid text-black shadow-[0_0_24px_rgba(204,255,0,0.35)]" : "bg-[#1E222A] text-[#565C6F]"}`}>
              {b.earned ? <Award className="w-6 h-6" /> : <Lock className="w-5 h-5" />}
            </div>
            <p className={`text-sm font-semibold mt-2 ${b.earned ? "" : "text-[#8A91A6]"}`}>{b.name}</p>
            <p className="text-[11px] text-[#565C6F] mt-0.5">{b.description}</p>
            {!b.earned && b.progress > 0 && (
              <div className="h-1 rounded-full bg-[#1E222A] mt-2 overflow-hidden"><div className="h-full bg-acid/70" style={{ width: `${b.progress * 100}%` }} /></div>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  );
}

export default function Performance() {
  return (
    <div className="p-4 md:p-8 space-y-6">
      <PageBanner image={IMG.progress} overline="performance" title="Your fitness, measured"
        subtitle="A level from 1 to 100 across strength, Olympic lifting, gymnastics, engine and metcons, the gaps holding you back, and every day you showed up."
        actions={<span className="inline-flex items-center gap-2 text-acid"><Flame className="w-5 h-5" /></span>} />
      <FitnessLevel />
      <Heatmap />
      <Badges />
    </div>
  );
}
